import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const XR_DETAILS: Record<string, {
  id: string; title: string; subtitle: string; color: string; icon: string;
  heroImg: string; overview: string; techStack: string[];
  specs: { label: string; value: string }[];
  useCases: { title: string; desc: string; detail: string; icon: string; img: string }[];
  roadmap: { year: string; event: string; status: 'done' | 'active' | 'future' }[];
  gallery: string[];
  stats: { label: string; value: string; icon: string }[];
}> = {
  ar: {
    id: 'ar', title: '增强现实 AR', subtitle: 'Augmented Reality', color: '#00FFCC', icon: 'ri-glasses-line',
    heroImg: 'https://public.readdy.ai/ai/img_res/10e4e3365c3a198c335b866d40c3ec69.jpg',
    overview: '增强现实（AR）技术将数字信息无缝叠加到真实世界，创造出虚实融合的全新感知体验。META66的AR技术基于自研的"光场引擎"，实现了0.1mm级别的空间定位精度和小于5ms的渲染延迟，让数字内容与真实世界的融合达到肉眼无法分辨的程度。\n\n我们的AR系统采用三层架构：感知层（空间理解+物体识别）、渲染层（光场计算+遮挡处理）、交互层（手势+眼动+语音）。这三层协同工作，让AR体验从"看到"升级为"感受到"。',
    techStack: ['光场引擎 LightField Engine', 'SLAM空间定位', '深度学习物体识别', '手势追踪 1000Hz', '眼动追踪 0.5°精度', '空间音频 3D', '6G实时传输', '边缘计算加速'],
    specs: [
      { label: '空间定位精度', value: '0.1mm' },
      { label: '渲染延迟', value: '<5ms' },
      { label: '视场角', value: '120°' },
      { label: '分辨率', value: '8K/眼' },
      { label: '帧率', value: '240fps' },
      { label: '识别物体数', value: '100万+类' },
    ],
    useCases: [
      {
        title: '工业AR维修系统', desc: '技术人员通过AR眼镜实时查看设备内部结构，维修效率提升300%',
        detail: '工业AR维修系统将设备的数字孪生模型实时叠加在真实设备上，技术人员可以"透视"设备内部，看到每一个零件的状态、温度和磨损程度。系统支持远程专家实时指导，专家的手势和标注会直接出现在技术人员的视野中。已在全球500+工厂部署，平均维修时间缩短65%，停机损失减少85%。',
        icon: 'ri-tools-line',
        img: 'https://readdy.ai/api/search-image?query=industrial%20AR%20augmented%20reality%20worker%20maintenance%20factory%20equipment%20holographic%20overlay%20cyan%20neon%20dark%20background%20futuristic%20technology%20cinematic%208k&width=600&height=400&seq=xr-ar-uc1&orientation=landscape',
      },
      {
        title: '医疗AR手术导航', desc: '外科医生在手术中实时看到患者器官的3D模型，手术精度达到0.1mm',
        detail: '医疗AR手术导航系统将患者的MRI/CT数据实时融合到手术视野中，外科医生可以在手术中看到血管、神经的精确位置，避免误伤。系统还支持AI辅助决策，实时提示最优切除路径。已获FDA和NMPA双重认证，在全球200+医院使用，手术并发症率降低60%。',
        icon: 'ri-heart-pulse-line',
        img: 'https://readdy.ai/api/search-image?query=medical%20AR%20surgery%20augmented%20reality%20doctor%20operating%20room%20holographic%20anatomy%20overlay%20cyan%20neon%20dark%20background%20futuristic%20technology%20cinematic%208k&width=600&height=400&seq=xr-ar-uc2&orientation=landscape',
      },
      {
        title: 'AR智能导航', desc: '将导航信息直接投影到道路上，驾驶员无需低头看屏幕',
        detail: 'AR智能导航将路线指引、危险预警、速度限制等信息直接叠加在驾驶员的视野中，完全消除了低头看导航的安全隐患。系统结合实时交通数据和AI预测，提前3-5秒预警危险路段。已与全球主要汽车品牌合作，搭载量超过500万辆，事故率降低40%。',
        icon: 'ri-map-pin-line',
        img: 'https://readdy.ai/api/search-image?query=AR%20navigation%20driving%20augmented%20reality%20windshield%20heads%20up%20display%20road%20directions%20cyan%20neon%20dark%20background%20futuristic%20technology%20cinematic%208k&width=600&height=400&seq=xr-ar-uc3&orientation=landscape',
      },
      {
        title: 'AR沉浸教育', desc: '学生可以与3D历史场景互动，让历史课变成身临其境的体验',
        detail: 'AR沉浸教育将抽象的知识转化为可交互的3D体验。学生可以在课桌上"召唤"出古罗马斗兽场，走进去探索；可以观察细胞分裂的全过程；可以与历史人物"对话"。已在全球10000+学校部署，学习效率提升3倍，知识留存率从20%提升至75%。',
        icon: 'ri-book-open-line',
        img: 'https://readdy.ai/api/search-image?query=AR%20education%20augmented%20reality%20classroom%20students%20holographic%203D%20learning%20history%20science%20cyan%20neon%20dark%20background%20futuristic%20technology%20cinematic%208k&width=600&height=400&seq=xr-ar-uc4&orientation=landscape',
      },
    ],
    roadmap: [
      { year: '2023', event: 'AR光场引擎1.0发布，实现0.5mm定位精度', status: 'done' },
      { year: '2024', event: 'META Vision AR眼镜量产，视场角达到90°', status: 'done' },
      { year: '2025', event: '光场引擎2.0，精度提升至0.1mm，视场角120°', status: 'active' },
      { year: '2026', event: 'AR隐形眼镜原型发布，重量<1g', status: 'future' },
      { year: '2028', event: 'AR隐形眼镜量产，全天候佩戴，续航12小时', status: 'future' },
      { year: '2030', event: 'AR与脑机接口融合，意念控制AR界面', status: 'future' },
    ],
    gallery: [
      'https://readdy.ai/api/search-image?query=AR%20augmented%20reality%20glasses%20wearable%20technology%20futuristic%20cyan%20neon%20dark%20background%20product%20design%208k&width=400&height=300&seq=xr-ar-g1&orientation=landscape',
      'https://readdy.ai/api/search-image?query=AR%20holographic%20interface%20floating%20screens%20hands%20interaction%20cyan%20neon%20dark%20background%20futuristic%208k&width=400&height=300&seq=xr-ar-g2&orientation=landscape',
      'https://readdy.ai/api/search-image?query=AR%20city%20navigation%20overlay%20real%20world%20digital%20information%20cyan%20neon%20dark%20background%20futuristic%208k&width=400&height=300&seq=xr-ar-g3&orientation=landscape',
      'https://readdy.ai/api/search-image?query=AR%20medical%20surgery%20holographic%20anatomy%20overlay%20operating%20room%20cyan%20neon%20dark%20background%20futuristic%208k&width=400&height=300&seq=xr-ar-g4&orientation=landscape',
    ],
    stats: [
      { label: '全球AR用户', value: '3.2亿', icon: 'ri-user-line' },
      { label: '企业部署', value: '50万+', icon: 'ri-building-line' },
      { label: '应用数量', value: '120万+', icon: 'ri-apps-line' },
      { label: '市场规模', value: '$3500亿', icon: 'ri-money-dollar-circle-line' },
    ],
  },
  vr: {
    id: 'vr', title: '虚拟现实 VR', subtitle: 'Virtual Reality', color: '#A855F7', icon: 'ri-vip-crown-line',
    heroImg: 'https://readdy.ai/api/search-image?query=virtual%20reality%20VR%20immersive%20world%20space%20exploration%20purple%20neon%20glowing%20particles%20dark%20background%20cinematic%208k%20ultra%20detailed%20wide%20panoramic&width=1400&height=600&seq=xr-vr-detail-hero&orientation=landscape',
    overview: '虚拟现实（VR）技术创造完全沉浸式的数字世界体验，让用户瞬间置身于任何场景。META66的VR系统采用"全感官沉浸"架构，不仅覆盖视觉和听觉，还通过触觉手套、气味模拟器和温度反馈系统，实现真正的五感沉浸体验。\n\n我们的VR渲染引擎基于光线追踪技术，实时计算每一束光线的物理路径，让虚拟世界的光影效果与真实世界无异。配合240Hz高刷新率和<1ms延迟，彻底消除了VR眩晕感。',
    techStack: ['光线追踪渲染引擎', '全感官反馈系统', '6DoF全身追踪', '触觉手套 1000触点', '气味模拟器', '温度反馈系统', '空间音频 Dolby Atmos', '量子芯片X1处理器'],
    specs: [
      { label: '分辨率', value: '16K/眼' },
      { label: '刷新率', value: '240Hz' },
      { label: '延迟', value: '<1ms' },
      { label: '视场角', value: '220°' },
      { label: '追踪精度', value: '0.01mm' },
      { label: '续航时间', value: '6小时' },
    ],
    useCases: [
      {
        title: 'VR太空旅行', desc: '足不出户即可体验国际空间站的失重感，探索火星表面',
        detail: 'VR太空旅行与NASA、ESA深度合作，使用真实的太空数据构建精确的太空环境。用户可以体验国际空间站的失重感、在火星表面漫步、近距离观察土星环。系统还原了太空中的真实物理效果，包括失重状态下液体的行为、太空服的压力感等。已有超过100万用户体验，满意度达99.2%。',
        icon: 'ri-rocket-line',
        img: 'https://readdy.ai/api/search-image?query=VR%20space%20travel%20virtual%20reality%20astronaut%20space%20station%20earth%20orbit%20purple%20neon%20dark%20background%20futuristic%20technology%20cinematic%208k&width=600&height=400&seq=xr-vr-uc1&orientation=landscape',
      },
      {
        title: 'VR社交元宇宙', desc: '在虚拟世界中与全球朋友面对面交流，感受真实的社交临场感',
        detail: 'VR社交元宇宙支持1000人同场互动，每个用户的虚拟形象通过AI实时捕捉1000个面部特征点，精确还原表情和情绪。用户可以在虚拟咖啡馆、音乐会、展览馆等场景中与全球朋友互动。系统还支持触觉握手——通过触觉手套，你可以真实感受到握手的力度。',
        icon: 'ri-group-line',
        img: 'https://readdy.ai/api/search-image?query=VR%20social%20metaverse%20virtual%20world%20people%20avatars%20meeting%20purple%20neon%20dark%20background%20futuristic%20technology%20cinematic%208k&width=600&height=400&seq=xr-vr-uc2&orientation=landscape',
      },
      {
        title: 'VR专业培训', desc: '飞行员、外科医生在VR中进行高风险操作训练，零风险高效率',
        detail: 'VR专业培训系统已被全球500+机构采用，覆盖飞行员、外科医生、消防员、核电站操作员等高风险职业。系统可以模拟各种极端情况，让学员在零风险环境中积累经验。训练效率比传统方式提升4倍，成本降低90%。已获得FAA、EASA等国际航空认证机构认可。',
        icon: 'ri-flight-takeoff-line',
        img: 'https://readdy.ai/api/search-image?query=VR%20training%20simulation%20pilot%20cockpit%20virtual%20reality%20professional%20training%20purple%20neon%20dark%20background%20futuristic%20technology%20cinematic%208k&width=600&height=400&seq=xr-vr-uc3&orientation=landscape',
      },
      {
        title: 'VR心理治疗', desc: '通过VR暴露疗法治疗恐惧症、PTSD，治愈率提升60%',
        detail: 'VR心理治疗已获FDA认证，用于治疗恐高症、社交恐惧症、PTSD等心理疾病。系统通过渐进式暴露疗法，让患者在安全的虚拟环境中逐步克服恐惧。与传统疗法相比，治疗周期缩短50%，治愈率提升60%，且无任何副作用。已在全球2000+心理诊所部署。',
        icon: 'ri-mental-health-line',
        img: 'https://readdy.ai/api/search-image?query=VR%20therapy%20mental%20health%20treatment%20virtual%20reality%20calm%20peaceful%20environment%20purple%20neon%20dark%20background%20futuristic%20technology%20cinematic%208k&width=600&height=400&seq=xr-vr-uc4&orientation=landscape',
      },
    ],
    roadmap: [
      { year: '2023', event: 'META VR Pro发布，240Hz刷新率，视场角200°', status: 'done' },
      { year: '2024', event: '全感官反馈系统上线，触觉手套量产', status: 'done' },
      { year: '2025', event: '16K分辨率实现，光线追踪实时渲染', status: 'active' },
      { year: '2026', event: '气味模拟器和温度反馈系统集成', status: 'future' },
      { year: '2028', event: 'VR设备重量降至100g以内，全天佩戴', status: 'future' },
      { year: '2030', event: 'VR与脑机接口融合，直接刺激感官神经', status: 'future' },
    ],
    gallery: [
      'https://readdy.ai/api/search-image?query=VR%20headset%20futuristic%20design%20purple%20neon%20dark%20background%20product%208k&width=400&height=300&seq=xr-vr-g1&orientation=landscape',
      'https://readdy.ai/api/search-image?query=VR%20virtual%20world%20immersive%20environment%20purple%20neon%20particles%20dark%20background%208k&width=400&height=300&seq=xr-vr-g2&orientation=landscape',
      'https://readdy.ai/api/search-image?query=VR%20haptic%20gloves%20touch%20feedback%20technology%20purple%20neon%20dark%20background%208k&width=400&height=300&seq=xr-vr-g3&orientation=landscape',
      'https://readdy.ai/api/search-image?query=VR%20social%20metaverse%20avatars%20virtual%20meeting%20purple%20neon%20dark%20background%208k&width=400&height=300&seq=xr-vr-g4&orientation=landscape',
    ],
    stats: [
      { label: '全球VR用户', value: '1.8亿', icon: 'ri-user-line' },
      { label: '内容时长', value: '500万小时', icon: 'ri-time-line' },
      { label: '开发者', value: '80万+', icon: 'ri-code-line' },
      { label: '市场规模', value: '$2200亿', icon: 'ri-money-dollar-circle-line' },
    ],
  },
  mr: {
    id: 'mr', title: '混合现实 MR', subtitle: 'Mixed Reality', color: '#FF6B35', icon: 'ri-magic-line',
    heroImg: 'https://readdy.ai/api/search-image?query=mixed%20reality%20MR%20holographic%20objects%20real%20environment%20interaction%20orange%20neon%20glow%20dark%20background%20cinematic%208k%20ultra%20detailed%20wide%20panoramic&width=1400&height=600&seq=xr-mr-detail-hero&orientation=landscape',
    overview: '混合现实（MR）是AR与VR的完美融合，数字物体不仅叠加在真实世界上，还能与真实环境实时交互——虚拟球会从真实桌子上弹起，虚拟人物会绕过真实的椅子。META66的MR技术基于"空间理解引擎"，能够实时理解真实环境的几何结构、光照条件和物理属性，让数字内容真正"融入"现实。\n\n我们的MR系统支持多人共享同一个混合现实空间，多个用户可以同时看到并操作同一个虚拟物体，实现前所未有的协作体验。',
    techStack: ['空间理解引擎', '实时光照估计', '物理碰撞模拟', '多人共享空间', '手势+语音+眼动融合', '毫米波深度传感器', '神经渲染技术', '量子加密传输'],
    specs: [
      { label: '空间精度', value: '1mm' },
      { label: '追踪速度', value: '1000Hz' },
      { label: '延迟', value: '<2ms' },
      { label: '识别率', value: '99.9%' },
      { label: '多人协作', value: '100人同场' },
      { label: '光照精度', value: '物理级' },
    ],
    useCases: [
      {
        title: '全息会议系统', desc: '与会者的全息投影出现在你的会议室，如同真实在场',
        detail: '全息会议系统让远程参会者的全息形象出现在本地会议室，与本地参会者共享同一个物理空间。系统支持全球100个地点同步，全息形象精度达到真人95%，包括微表情、手势和肢体语言。已被全球500强企业广泛采用，差旅成本降低70%，会议效率提升200%。',
        icon: 'ri-video-chat-line',
        img: 'https://readdy.ai/api/search-image?query=MR%20holographic%20meeting%20conference%20room%20virtual%20presence%20orange%20neon%20dark%20background%20futuristic%20technology%20cinematic%208k&width=600&height=400&seq=xr-mr-uc1&orientation=landscape',
      },
      {
        title: 'MR建筑设计', desc: '建筑师在真实场地上叠加虚拟建筑模型，实时调整设计方案',
        detail: 'MR建筑设计让建筑师在真实场地上"建造"虚拟建筑，实时查看建筑与周边环境的关系，调整设计方案。系统支持多人协作，建筑师、结构工程师、室内设计师可以同时在同一个虚拟建筑中工作。设计周期缩短60%，客户满意度提升40%，建筑错误率降低80%。',
        icon: 'ri-building-2-line',
        img: 'https://readdy.ai/api/search-image?query=MR%20architecture%20design%20building%20holographic%20model%20real%20site%20orange%20neon%20dark%20background%20futuristic%20technology%20cinematic%208k&width=600&height=400&seq=xr-mr-uc2&orientation=landscape',
      },
      {
        title: 'MR零售体验', desc: '在家中试穿虚拟服装，或将家具放置在真实房间中预览效果',
        detail: 'MR零售体验让消费者在购买前就能看到产品在真实环境中的效果。服装可以虚拟试穿，家具可以放置在真实房间中预览，汽车可以停在自家车道上查看颜色和尺寸。退货率降低35%，转化率提升200%，已被全球1000+品牌采用，包括IKEA、Nike、BMW等。',
        icon: 'ri-shopping-bag-line',
        img: 'https://readdy.ai/api/search-image?query=MR%20retail%20shopping%20virtual%20try-on%20furniture%20placement%20home%20orange%20neon%20dark%20background%20futuristic%20technology%20cinematic%208k&width=600&height=400&seq=xr-mr-uc3&orientation=landscape',
      },
      {
        title: 'MR远程协作', desc: '工程师在不同地点共同操作同一个虚拟设备模型',
        detail: 'MR远程协作让分布在全球各地的工程师可以共同操作同一个虚拟设备模型，进行设计评审、故障诊断和维修指导。系统支持实时标注、测量和模拟，协作效率提升300%，差旅成本降低70%。已在航空、汽车、能源等行业广泛应用。',
        icon: 'ri-team-line',
        img: 'https://readdy.ai/api/search-image?query=MR%20remote%20collaboration%20engineers%20virtual%20model%20holographic%20orange%20neon%20dark%20background%20futuristic%20technology%20cinematic%208k&width=600&height=400&seq=xr-mr-uc4&orientation=landscape',
      },
    ],
    roadmap: [
      { year: '2023', event: 'META MR One发布，空间精度5mm', status: 'done' },
      { year: '2024', event: '多人共享空间功能上线，支持10人同场', status: 'done' },
      { year: '2025', event: '空间精度提升至1mm，支持100人同场协作', status: 'active' },
      { year: '2026', event: '物理级光照模拟，虚实融合达到照片级真实', status: 'future' },
      { year: '2028', event: 'MR眼镜重量降至50g，外观与普通眼镜无异', status: 'future' },
      { year: '2030', event: 'MR隐形眼镜量产，完全无感佩戴', status: 'future' },
    ],
    gallery: [
      'https://readdy.ai/api/search-image?query=MR%20mixed%20reality%20headset%20design%20orange%20neon%20dark%20background%20product%208k&width=400&height=300&seq=xr-mr-g1&orientation=landscape',
      'https://readdy.ai/api/search-image?query=MR%20holographic%20objects%20real%20world%20interaction%20orange%20neon%20dark%20background%208k&width=400&height=300&seq=xr-mr-g2&orientation=landscape',
      'https://readdy.ai/api/search-image?query=MR%20collaboration%20multiple%20users%20shared%20space%20orange%20neon%20dark%20background%208k&width=400&height=300&seq=xr-mr-g3&orientation=landscape',
      'https://readdy.ai/api/search-image?query=MR%20retail%20virtual%20furniture%20home%20preview%20orange%20neon%20dark%20background%208k&width=400&height=300&seq=xr-mr-g4&orientation=landscape',
    ],
    stats: [
      { label: '企业客户', value: '10万+', icon: 'ri-building-line' },
      { label: '协作效率提升', value: '300%', icon: 'ri-speed-line' },
      { label: '差旅成本降低', value: '70%', icon: 'ri-plane-line' },
      { label: '市场规模', value: '$1800亿', icon: 'ri-money-dollar-circle-line' },
    ],
  },
  spatial: {
    id: 'spatial', title: '空间计算', subtitle: 'Spatial Computing', color: '#FFD700', icon: 'ri-layout-3-line',
    heroImg: 'https://readdy.ai/api/search-image?query=spatial%20computing%20hand%20gesture%20control%20holographic%20interface%20floating%20screens%20gold%20yellow%20neon%20dark%20background%20cinematic%208k%20ultra%20detailed%20wide%20panoramic&width=1400&height=600&seq=xr-spatial-detail-hero&orientation=landscape',
    overview: '空间计算是继PC、移动互联网之后的第三次计算平台革命，将整个物理空间变成交互界面。META66的空间计算平台"NEXUS OS"支持眼动追踪、手势识别、语音控制和脑机接口四种输入方式，让计算无处不在，彻底解放双手。\n\n NEXUS OS采用"意图理解"AI引擎，能够预测用户的下一步操作，提前加载相关内容，实现真正的"零等待"体验。系统支持无限数量的虚拟屏幕，用户可以在任何空间中展开完整的工作环境。',
    techStack: ['NEXUS OS空间操作系统', '意图理解AI引擎', '眼动追踪 0.5°精度', '手势识别 0.1ms延迟', '语音识别 99.5%准确率', '脑机接口初代集成', '空间音频定位', '量子加密通信'],
    specs: [
      { label: '手势识别延迟', value: '0.1ms' },
      { label: '眼动追踪精度', value: '0.5°' },
      { label: '语音识别率', value: '99.5%' },
      { label: '虚拟屏幕数', value: '无限' },
      { label: '空间范围', value: '无限' },
      { label: '多任务数', value: '100+同时' },
    ],
    useCases: [
      {
        title: '空间办公系统', desc: '在任何空间中展开无限大的虚拟工作台，多任务处理效率提升500%',
        detail: '空间办公系统让用户在任何地方——家里、咖啡馆、公园——都能展开完整的工作环境。100个虚拟屏幕可以同时显示，AI自动整理工作流，将相关内容聚合在一起。系统还支持与同事的实时协作，多人可以在同一个虚拟工作空间中共同工作。效率提升5倍，已被全球100万+企业用户采用。',
        icon: 'ri-computer-line',
        img: 'https://readdy.ai/api/search-image?query=spatial%20computing%20office%20workspace%20holographic%20screens%20floating%20gold%20yellow%20neon%20dark%20background%20futuristic%20technology%20cinematic%208k&width=600&height=400&seq=xr-spatial-uc1&orientation=landscape',
      },
      {
        title: '空间创意设计', desc: '用双手在空中雕刻3D模型，直觉式创作体验',
        detail: '空间创意设计让艺术家和设计师用双手在空中直接创作3D内容，就像雕塑家用手捏泥一样自然。系统支持精确的手势操控，可以创作出毫米级精度的3D模型。AI辅助功能可以根据设计师的意图自动补全细节，设计速度提升3倍。已被全球顶级设计公司和游戏工作室采用。',
        icon: 'ri-pencil-ruler-2-line',
        img: 'https://readdy.ai/api/search-image?query=spatial%20computing%203D%20design%20creation%20hands%20gesture%20holographic%20model%20gold%20yellow%20neon%20dark%20background%20futuristic%20technology%20cinematic%208k&width=600&height=400&seq=xr-spatial-uc2&orientation=landscape',
      },
      {
        title: '空间沉浸娱乐', desc: '游戏场景延伸到整个房间，玩家真实移动参与游戏',
        detail: '空间沉浸娱乐将游戏场景延伸到整个物理空间，玩家需要真实移动、跳跃、躲避来参与游戏。系统将家具和墙壁转化为游戏元素，让客厅变成战场、厨房变成迷宫。全身运动追踪确保每一个动作都被精确捕捉，卡路里消耗是传统游戏的10倍，健康娱乐两不误。',
        icon: 'ri-gamepad-line',
        img: 'https://readdy.ai/api/search-image?query=spatial%20computing%20gaming%20room%20scale%20holographic%20game%20environment%20gold%20yellow%20neon%20dark%20background%20futuristic%20technology%20cinematic%208k&width=600&height=400&seq=xr-spatial-uc3&orientation=landscape',
      },
      {
        title: '空间智能家居', desc: '用眼神和手势控制家中所有智能设备，无需任何遥控器',
        detail: '空间智能家居让用户用眼神和手势控制家中所有智能设备。看向灯，它就亮；挥手，窗帘就关；说"播放音乐"，音乐就响起。系统与10000+智能设备兼容，AI学习用户习惯，自动化程度达到95%。用户平均每天节省30分钟的设备操作时间。',
        icon: 'ri-home-smile-line',
        img: 'https://readdy.ai/api/search-image?query=spatial%20computing%20smart%20home%20control%20gesture%20eye%20tracking%20holographic%20interface%20gold%20yellow%20neon%20dark%20background%20futuristic%20technology%20cinematic%208k&width=600&height=400&seq=xr-spatial-uc4&orientation=landscape',
      },
    ],
    roadmap: [
      { year: '2023', event: 'NEXUS OS 1.0发布，支持手势和语音控制', status: 'done' },
      { year: '2024', event: '眼动追踪集成，精度达到1°', status: 'done' },
      { year: '2025', event: 'NEXUS OS 2.0，意图理解AI引擎上线', status: 'active' },
      { year: '2026', event: '脑机接口初代集成，支持意念控制基础操作', status: 'future' },
      { year: '2028', event: '空间计算设备价格降至3000元，大众普及', status: 'future' },
      { year: '2030', event: '空间计算取代智能手机成为主流计算平台', status: 'future' },
    ],
    gallery: [
      'https://readdy.ai/api/search-image?query=spatial%20computing%20device%20wearable%20gold%20yellow%20neon%20dark%20background%20product%208k&width=400&height=300&seq=xr-spatial-g1&orientation=landscape',
      'https://readdy.ai/api/search-image?query=spatial%20computing%20gesture%20control%20holographic%20interface%20gold%20yellow%20neon%20dark%20background%208k&width=400&height=300&seq=xr-spatial-g2&orientation=landscape',
      'https://readdy.ai/api/search-image?query=spatial%20computing%20office%20workspace%20multiple%20screens%20gold%20yellow%20neon%20dark%20background%208k&width=400&height=300&seq=xr-spatial-g3&orientation=landscape',
      'https://readdy.ai/api/search-image?query=spatial%20computing%20smart%20home%20control%20eye%20tracking%20gold%20yellow%20neon%20dark%20background%208k&width=400&height=300&seq=xr-spatial-g4&orientation=landscape',
    ],
    stats: [
      { label: '效率提升', value: '500%', icon: 'ri-speed-line' },
      { label: '企业用户', value: '100万+', icon: 'ri-building-line' },
      { label: '兼容设备', value: '10000+', icon: 'ri-device-line' },
      { label: '市场规模', value: '$1.7万亿', icon: 'ri-money-dollar-circle-line' },
    ],
  },
};

export default function XRDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<'overview' | 'usecases' | 'roadmap' | 'gallery'>('overview');
  const [selectedCase, setSelectedCase] = useState<number | null>(null);
  const [animIn, setAnimIn] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const detail = id ? XR_DETAILS[id] : null;

  useEffect(() => {
    window.scrollTo(0, 0);
    setTimeout(() => setAnimIn(true), 100);
    if (detail) {
      document.title = `${detail.title} | META66 XR扩展现实深度解析 · ${detail.subtitle}`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.setAttribute('content', `META66 ${detail.title}技术深度解析：${detail.overview.slice(0, 120)}...`);
      const metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaKeywords) metaKeywords.setAttribute('content', `${detail.title}, ${detail.subtitle}, META66, 元宇宙, XR技术, ${detail.techStack.slice(0, 3).join(', ')}`);
    }
  }, [id, detail]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !detail) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    const particles: { x: number; y: number; vx: number; vy: number; r: number; alpha: number }[] = [];
    for (let i = 0; i < 80; i++) {
      particles.push({ x: Math.random() * canvas.width, y: Math.random() * canvas.height, vx: (Math.random() - 0.5) * 0.3, vy: (Math.random() - 0.5) * 0.3, r: Math.random() * 1.5 + 0.3, alpha: Math.random() * 0.2 + 0.05 });
    }
    let animId: number;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${detail.color === '#00FFCC' ? '0,255,204' : detail.color === '#A855F7' ? '168,85,247' : detail.color === '#FF6B35' ? '255,107,53' : '255,215,0'},${p.alpha})`;
        ctx.fill();
      });
      animId = requestAnimationFrame(animate);
    };
    animate();
    return () => cancelAnimationFrame(animId);
  }, [id, detail]);

  if (!detail) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#020810' }}>
        <div className="text-center">
          <p className="text-white/50 text-xl mb-4">页面不存在</p>
          <button onClick={() => navigate('/xr')} className="px-6 py-3 rounded-xl font-bold text-black cursor-pointer" style={{ background: '#00FFCC' }}>返回XR页面</button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: '技术概述', icon: 'ri-information-line' },
    { id: 'usecases', label: '应用场景', icon: 'ri-apps-line' },
    { id: 'roadmap', label: '发展路线', icon: 'ri-map-pin-time-line' },
    { id: 'gallery', label: '图片展示', icon: 'ri-image-line' },
  ] as const;

  return (
    <div className="min-h-screen overflow-hidden" style={{ background: '#020810', fontFamily: "'Inter', 'PingFang SC', sans-serif" }}>
      <canvas ref={canvasRef} className="fixed inset-0 pointer-events-none z-0" />

      {/* Fixed BG */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: `linear-gradient(${detail.color}20 1px, transparent 1px), linear-gradient(90deg, ${detail.color}20 1px, transparent 1px)`,
          backgroundSize: '50px 50px',
        }} />
        <div className="absolute inset-0" style={{ background: `radial-gradient(ellipse at 50% 0%, ${detail.color}06 0%, transparent 60%)` }} />
      </div>

      {/* Header */}
      <div className="sticky top-0 z-50" style={{ background: 'rgba(2,8,16,0.95)', borderBottom: `1px solid ${detail.color}20`, backdropFilter: 'blur(20px)' }}>
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/xr')} className="flex items-center gap-2 transition-all cursor-pointer whitespace-nowrap hover:scale-105" style={{ color: 'rgba(255,255,255,0.5)' }}>
              <i className="ri-arrow-left-line" />
              <span className="text-sm">返回XR</span>
            </button>
            <span className="text-white/20">/</span>
            <span className="text-sm font-bold" style={{ color: detail.color }}>{detail.title}</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center" style={{ background: `${detail.color}20`, border: `1.5px solid ${detail.color}50`, borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%' }}>
              <i className={`${detail.icon} text-white text-sm`} />
            </div>
            <span className="font-black text-white" style={{ fontFamily: 'Orbitron, sans-serif', fontSize: '0.9rem' }}>XR · {detail.subtitle.toUpperCase()}</span>
          </div>
          <div className="text-sm" style={{ color: `${detail.color}70`, fontFamily: 'Caveat, cursive' }}>META66 · 深度解析</div>
        </div>
      </div>

      {/* Hero */}
      <div className="relative z-10 h-[480px] overflow-hidden">
        <img src={detail.heroImg} alt={detail.title} className="w-full h-full object-cover object-top" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(2,8,16,0.2) 0%, rgba(2,8,16,0.5) 50%, rgba(2,8,16,1) 100%)' }} />
        <div className="absolute inset-0" style={{ background: `radial-gradient(ellipse at 30% 50%, ${detail.color}10 0%, transparent 60%)` }} />

        <div className="absolute inset-0 flex items-center px-8 md:px-16">
          <div style={{ opacity: animIn ? 1 : 0, transform: animIn ? 'translateX(0)' : 'translateX(-40px)', transition: 'all 0.9s ease' }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-5" style={{ background: `${detail.color}15`, border: `1.5px solid ${detail.color}40`, borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%' }}>
              <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: detail.color }} />
              <span className="text-sm font-bold" style={{ color: detail.color, fontFamily: 'Caveat, cursive', fontSize: '0.95rem' }}>XR DEEP DIVE · {detail.subtitle}</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-black text-white mb-4 leading-tight" style={{ fontFamily: 'Orbitron, sans-serif' }}>
              <span className="text-transparent bg-clip-text" style={{ backgroundImage: `linear-gradient(135deg, ${detail.color}, white)` }}>{detail.subtitle.split(' ')[0]}</span>
            </h1>
            <h2 className="text-2xl md:text-3xl font-black text-white mb-4">{detail.title}</h2>

            {/* Quick Stats */}
            <div className="flex flex-wrap gap-4 mt-6">
              {detail.stats.map(s => (
                <div key={s.label} className="px-4 py-2" style={{ background: 'rgba(2,8,16,0.7)', border: `1px solid ${detail.color}30`, borderRadius: '10px 8px 12px 8px / 8px 12px 8px 10px', backdropFilter: 'blur(10px)' }}>
                  <p className="font-black text-lg" style={{ color: detail.color, fontFamily: 'Orbitron, sans-serif' }}>{s.value}</p>
                  <p className="text-white/40 text-xs">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="sticky z-40" style={{ top: '65px', background: 'rgba(2,8,16,0.95)', borderBottom: `1px solid ${detail.color}15`, backdropFilter: 'blur(20px)' }}>
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="flex gap-1 py-3">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all whitespace-nowrap cursor-pointer"
                style={{
                  background: activeTab === tab.id ? `${detail.color}15` : 'transparent',
                  color: activeTab === tab.id ? detail.color : 'rgba(255,255,255,0.4)',
                  border: `1px solid ${activeTab === tab.id ? detail.color + '40' : 'transparent'}`,
                }}
              >
                <i className={tab.icon} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 py-12">

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 flex flex-col gap-6">
              <div className="p-8 rounded-2xl" style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${detail.color}15`, borderRadius: '18px 14px 20px 16px / 14px 20px 16px 18px' }}>
                <h3 className="text-white font-black text-xl mb-5 flex items-center gap-3" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                  <i className="ri-information-line" style={{ color: detail.color }} />技术概述
                </h3>
                {detail.overview.split('\n\n').map((para, i) => (
                  <p key={i} className="text-white/60 leading-relaxed text-base mb-4 last:mb-0">{para}</p>
                ))}
              </div>

              {/* Tech Stack */}
              <div className="p-8 rounded-2xl" style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${detail.color}15`, borderRadius: '18px 14px 20px 16px / 14px 20px 16px 18px' }}>
                <h3 className="text-white font-black text-xl mb-5 flex items-center gap-3" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                  <i className="ri-stack-line" style={{ color: detail.color }} />核心技术栈
                </h3>
                <div className="flex flex-wrap gap-3">
                  {detail.techStack.map(tech => (
                    <span key={tech} className="px-4 py-2 text-sm font-semibold" style={{ background: `${detail.color}10`, color: detail.color, border: `1px solid ${detail.color}25`, borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%', fontFamily: 'Caveat, cursive', fontSize: '0.9rem' }}>{tech}</span>
                  ))}
                </div>
              </div>
            </div>

            {/* Specs */}
            <div className="flex flex-col gap-4">
              <div className="p-6 rounded-2xl" style={{ background: `${detail.color}06`, border: `1.5px solid ${detail.color}20`, borderRadius: '18px 14px 20px 16px / 14px 20px 16px 18px' }}>
                <h3 className="text-white font-black text-lg mb-5" style={{ fontFamily: 'Orbitron, sans-serif' }}>技术规格</h3>
                {detail.specs.map(spec => (
                  <div key={spec.label} className="flex justify-between items-center py-3 border-b" style={{ borderColor: `${detail.color}10` }}>
                    <span className="text-white/50 text-sm">{spec.label}</span>
                    <span className="font-black text-base" style={{ color: detail.color, fontFamily: 'Orbitron, sans-serif' }}>{spec.value}</span>
                  </div>
                ))}
              </div>

              {/* Related XR Modes */}
              <div className="p-6 rounded-2xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '18px 14px 20px 16px / 14px 20px 16px 18px' }}>
                <h3 className="text-white font-black text-lg mb-4">探索其他XR技术</h3>
                {Object.values(XR_DETAILS).filter(d => d.id !== detail.id).map(d => (
                  <button key={d.id} onClick={() => navigate(`/xr/${d.id}`)} className="w-full flex items-center gap-3 p-3 rounded-xl mb-2 last:mb-0 cursor-pointer transition-all hover:-translate-y-0.5" style={{ background: `${d.color}06`, border: `1px solid ${d.color}15` }}>
                    <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" style={{ background: `${d.color}15`, borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%' }}>
                      <i className={`${d.icon} text-sm`} style={{ color: d.color }} />
                    </div>
                    <span className="text-white/60 text-sm font-medium">{d.title}</span>
                    <i className="ri-arrow-right-line ml-auto text-xs" style={{ color: d.color }} />
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Use Cases Tab */}
        {activeTab === 'usecases' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {detail.useCases.map((uc, i) => (
              <div
                key={uc.title}
                className="group rounded-2xl overflow-hidden cursor-pointer transition-all duration-400 hover:-translate-y-2"
                style={{ background: 'rgba(255,255,255,0.02)', border: `1.5px solid ${detail.color}20`, borderRadius: '18px 14px 20px 16px / 14px 20px 16px 18px', opacity: animIn ? 1 : 0, transform: animIn ? 'translateY(0)' : 'translateY(30px)', transition: `all 0.6s ease ${i * 100}ms` }}
                onClick={() => setSelectedCase(selectedCase === i ? null : i)}
              >
                <div className="relative h-52 overflow-hidden">
                  <img src={uc.img} alt={uc.title} className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, transparent 30%, rgba(2,8,16,0.95) 100%)' }} />
                  <div className="absolute top-3 left-3 w-10 h-10 flex items-center justify-center" style={{ background: `${detail.color}20`, border: `1.5px solid ${detail.color}40`, borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%' }}>
                    <i className={`${uc.icon} text-lg`} style={{ color: detail.color }} />
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-white font-black text-lg mb-2">{uc.title}</h3>
                  <p className="text-white/50 text-sm leading-relaxed mb-4">{uc.desc}</p>
                  {selectedCase === i && (
                    <div className="p-4 rounded-xl mb-4" style={{ background: `${detail.color}08`, border: `1px solid ${detail.color}20` }}>
                      <p className="text-white/65 text-sm leading-relaxed">{uc.detail}</p>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm font-bold" style={{ color: detail.color }}>
                    <span>{selectedCase === i ? '收起详情' : '查看深度解析'}</span>
                    <i className={selectedCase === i ? 'ri-arrow-up-line' : 'ri-arrow-down-line'} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Roadmap Tab */}
        {activeTab === 'roadmap' && (
          <div className="max-w-3xl mx-auto">
            <div className="relative">
              <div className="absolute left-8 top-0 bottom-0 w-px" style={{ background: `linear-gradient(to bottom, ${detail.color}60, transparent)` }} />
              {detail.roadmap.map((item, i) => (
                <div key={item.year} className="flex items-start gap-6 mb-8 last:mb-0" style={{ opacity: animIn ? 1 : 0, transform: animIn ? 'translateX(0)' : 'translateX(-30px)', transition: `all 0.6s ease ${i * 100}ms` }}>
                  <div className="w-16 h-16 flex items-center justify-center flex-shrink-0 z-10 font-black text-sm" style={{
                    background: item.status === 'done' ? detail.color : item.status === 'active' ? `${detail.color}30` : 'rgba(255,255,255,0.05)',
                    border: `2px solid ${item.status === 'future' ? 'rgba(255,255,255,0.1)' : detail.color}`,
                    borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%',
                    color: item.status === 'done' ? '#000' : item.status === 'active' ? detail.color : 'rgba(255,255,255,0.3)',
                    fontFamily: 'Orbitron, sans-serif',
                    fontSize: '0.7rem',
                  }}>
                    {item.status === 'done' ? <i className="ri-check-line text-xl" /> : item.year.slice(2)}
                  </div>
                  <div className="flex-1 pt-3 pb-6 border-b" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-black text-xl" style={{ color: item.status === 'future' ? 'rgba(255,255,255,0.3)' : detail.color, fontFamily: 'Orbitron, sans-serif' }}>{item.year}</span>
                      <span className="px-2 py-0.5 text-xs font-bold rounded-full" style={{
                        background: item.status === 'done' ? `${detail.color}20` : item.status === 'active' ? `${detail.color}15` : 'rgba(255,255,255,0.05)',
                        color: item.status === 'done' ? detail.color : item.status === 'active' ? detail.color : 'rgba(255,255,255,0.2)',
                        border: `1px solid ${item.status === 'future' ? 'rgba(255,255,255,0.08)' : detail.color + '30'}`,
                      }}>
                        {item.status === 'done' ? '已完成' : item.status === 'active' ? '进行中' : '规划中'}
                      </span>
                    </div>
                    <p className="text-white/60 text-base leading-relaxed">{item.event}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Gallery Tab */}
        {activeTab === 'gallery' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {detail.gallery.map((img, i) => (
              <div key={i} className="relative rounded-2xl overflow-hidden group cursor-pointer" style={{ height: '280px', border: `1.5px solid ${detail.color}20`, borderRadius: '18px 14px 20px 16px / 14px 20px 16px 18px', opacity: animIn ? 1 : 0, transform: animIn ? 'translateY(0)' : 'translateY(30px)', transition: `all 0.6s ease ${i * 100}ms` }}>
                <img src={img} alt={`${detail.title} ${i + 1}`} className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: `${detail.color}15` }} />
                <div className="absolute bottom-4 left-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span className="px-3 py-1.5 text-xs font-bold" style={{ background: `${detail.color}20`, color: detail.color, border: `1px solid ${detail.color}40`, borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%' }}>{detail.title} · 图{i + 1}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
